import { PDFDocument } from 'pdf-lib'
import { useEffect, useRef, useState } from 'react'

import UploadIcon from '@/components/icons/upload.svg?react'
import { Button } from '@/components/ui/button'

interface Props {
  onChangeFile: (file: File | null) => void
  previewUrl?: string
}

type FilePreview = {
  file: File
  preview: string | null // Blob URL for images, null for PDFs
}

export const UploadArea = ({ onChangeFile, previewUrl }: Props) => {
  // ===== State =====
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([])
  const [mergedFile, setMergedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isMerging, setIsMerging] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isPreviewUrlHidden, setIsPreviewUrlHidden] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // ===== Computed Values =====
  const isPreviewPdf = previewUrl?.toLowerCase().endsWith('.pdf') ?? false
  const isPreviewImage = previewUrl && !isPreviewPdf
  const showPreview = uploadedFiles.length > 0 || (previewUrl && !isPreviewUrlHidden)

  // ===== Effects =====

  /** Cleanup blob URLs when component unmounts to prevent memory leaks */
  useEffect(() => {
    return () => {
      filePreviews.forEach(({ preview }) => {
        if (preview?.startsWith('blob:')) {
          URL.revokeObjectURL(preview)
        }
      })
    }
  }, [filePreviews])

  /** Render Adobe PDF viewer for existing preview URL (from server) */
  useEffect(() => {
    if (!previewUrl || !isPreviewPdf || uploadedFiles.length > 0) return

    const renderPdfPreview = () => {
      const adobeDiv = document.getElementById('adobe-pdf-view')
      if (!adobeDiv) return

      adobeDiv.innerHTML = ''
      // @ts-expect-error - Adobe DC View is loaded globally
      const adobeDCView = new AdobeDC.View({
        clientId: '3daf1a0430a045879e5bf8163cad53c3',
        divId: 'adobe-pdf-view',
      })
      adobeDCView.previewFile(
        {
          content: { location: { url: previewUrl } },
          metaData: { fileName: 'document.pdf' },
        },
        { embedMode: 'IN_LINE' }
      )
    }

    setTimeout(renderPdfPreview, 100)
  }, [previewUrl, isPreviewPdf, uploadedFiles.length])

  /** Render Adobe PDF viewer for uploaded single PDF file */
  useEffect(() => {
    const singlePdfFile = filePreviews.length === 1 && !filePreviews[0].preview ? filePreviews[0].file : null
    if (!singlePdfFile || singlePdfFile.type !== 'application/pdf') return

    const renderUploadedPdf = () => {
      const adobeDiv = document.getElementById('adobe-pdf-view-uploaded')
      if (!adobeDiv) return

      adobeDiv.innerHTML = ''
      // @ts-expect-error - Adobe DC View is loaded globally
      const adobeDCView = new AdobeDC.View({
        clientId: '3daf1a0430a045879e5bf8163cad53c3',
        divId: 'adobe-pdf-view-uploaded',
      })

      const reader = new FileReader()
      reader.onloadend = e => {
        const filePromise = Promise.resolve(e.target?.result)
        adobeDCView.previewFile(
          {
            content: { promise: filePromise },
            metaData: { fileName: singlePdfFile.name },
          },
          { embedMode: 'IN_LINE' }
        )
      }
      reader.readAsArrayBuffer(singlePdfFile)
    }

    setTimeout(renderUploadedPdf, 100)
  }, [filePreviews])

  // ===== PDF Processing Functions =====

  /** Convert image file (JPG/PNG) to PDF format */
  const imageToPdf = async (file: File): Promise<Uint8Array> => {
    const pdfDoc = await PDFDocument.create()
    const imageBytes = await file.arrayBuffer()

    // Embed image based on type
    const image =
      file.type === 'image/jpeg' || file.type === 'image/jpg'
        ? await pdfDoc.embedJpg(imageBytes)
        : file.type === 'image/png'
          ? await pdfDoc.embedPng(imageBytes)
          : null

    if (!image) throw new Error(`Unsupported image type: ${file.type}`)

    // Create page matching image dimensions
    const page = pdfDoc.addPage([image.width, image.height])
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height })

    return await pdfDoc.save()
  }

  /** Merge multiple files (PDFs and images) into a single PDF */
  const mergeFilesToPdf = async (files: File[]): Promise<File> => {
    const mergedPdf = await PDFDocument.create()

    // Process each file: convert images to PDF, then merge all pages
    for (const file of files) {
      const pdfBytes = file.type === 'application/pdf' ? await file.arrayBuffer() : await imageToPdf(file)
      const pdf = await PDFDocument.load(pdfBytes)
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
      pages.forEach(page => mergedPdf.addPage(page))
    }

    const mergedBytes = await mergedPdf.save()
    const blob = new Blob([new Uint8Array(mergedBytes)], { type: 'application/pdf' })
    return new File([blob], 'receipts.pdf', { type: 'application/pdf' })
  }

  // ===== Validation & File Handlers =====

  /** Validate file type - only images (JPG/PNG) and PDFs allowed */
  const validateFile = (file: File): boolean => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    return allowedTypes.includes(file.type)
  }

  /** Handle new file selection - validate, create previews, and merge into single PDF */
  const handleFilesSelect = async (newFiles: FileList | File[]) => {
    const filesArray = Array.from(newFiles)
    const validFiles: File[] = []
    const invalidFiles: string[] = []

    // Separate valid and invalid files
    filesArray.forEach(file => {
      if (validateFile(file)) {
        validFiles.push(file)
      } else {
        invalidFiles.push(file.name)
      }
    })

    // Handle validation errors
    if (invalidFiles.length > 0) {
      setErrorMessage(`קבצים לא תקינים (רק תמונות ו-PDF מותרים): ${invalidFiles.join(', ')}`)
      if (validFiles.length === 0) return
    } else {
      setErrorMessage('')
    }

    setIsPreviewUrlHidden(true) // Hide existing preview URL when adding new files
    const allFiles = [...uploadedFiles, ...validFiles]
    setUploadedFiles(allFiles)

    // Create blob URLs for image previews
    const newPreviews: FilePreview[] = validFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }))
    setFilePreviews([...filePreviews, ...newPreviews])

    // Merge all files into a single PDF
    try {
      setIsMerging(true)
      const merged = await mergeFilesToPdf(allFiles)
      setMergedFile(merged)
      onChangeFile(merged)
      if (invalidFiles.length === 0) setErrorMessage('')
    } catch (error) {
      console.error('Merge error:', error)
      setErrorMessage(`שגיאה במיזוג הקבצים: ${error instanceof Error ? error.message : 'נסה שוב'}`)
    } finally {
      setIsMerging(false)
    }
  }

  // ===== Event Handlers =====

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files?.length) handleFilesSelect(files)
    event.target.value = '' // Reset input for re-selection
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files.length) handleFilesSelect(e.dataTransfer.files)
  }

  const handleClick = () => fileInputRef.current?.click()

  /** Remove a specific file from the list and re-merge remaining files */
  const handleRemoveFile = async (indexToRemove: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== indexToRemove)
    const newPreviews = filePreviews.filter((_, i) => i !== indexToRemove)

    // Cleanup removed preview blob URL
    const removed = filePreviews[indexToRemove]
    if (removed?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.preview)
    }

    setUploadedFiles(newFiles)
    setFilePreviews(newPreviews)

    if (newFiles.length === 0) {
      setMergedFile(null)
      onChangeFile(null)
    } else {
      // Re-merge remaining files
      try {
        setIsMerging(true)
        const merged = await mergeFilesToPdf(newFiles)
        setMergedFile(merged)
        onChangeFile(merged)
      } catch (error) {
        console.error('Re-merge error:', error)
        setErrorMessage('שגיאה במיזוג הקבצים. נסה שוב.')
      } finally {
        setIsMerging(false)
      }
    }
  }

  /** Remove all files and reset the component */
  const handleRemoveAll = () => {
    filePreviews.forEach(({ preview }) => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview)
    })

    setUploadedFiles([])
    setFilePreviews([])
    setMergedFile(null)
    setErrorMessage('')
    setIsPreviewUrlHidden(true) // Hide existing preview URL
    onChangeFile(null)
  }

  // ===== Render =====

  return (
    <div className='space-y-4'>
      {/* Hidden file input - always available for "הוסף עוד" button */}
      <input ref={fileInputRef} type='file' accept='image/*,.pdf' multiple onChange={handleFileUpload} className='hidden' />

      {/* Error Notification - Shows validation and merge errors */}
      {errorMessage && (
        <div className='rounded-lg border border-red-200 bg-red-50 p-3'>
          <p className='text-sm text-red-800'>{errorMessage}</p>
        </div>
      )}

      {/* Upload Drop Zone - Shown when no files uploaded */}
      {!showPreview && (
        <div
          className={`border-border-default flex h-[200px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-white p-8 text-center transition-colors ${
            isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <UploadIcon className={`h-6 w-6 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className='text-text-secondary text-sm'>גרור לכאן קבצים או לחץ לבחירה</p>
          <p className='text-text-placeholder text-xs'>ניתן להעלות מספר תמונות או PDF - יאוחדו לקובץ אחד</p>
        </div>
      )}

      {/* Preview Section - Shows uploaded files or existing preview URL */}
      {showPreview && (
        <div className='rounded-lg bg-white p-4 shadow-sm'>
          {/* Header: File count, merge status, action buttons */}
          <div className='mb-3 flex items-center justify-between'>
            <div className='grow'>
              {uploadedFiles.length > 0 && (
                <div>
                  <p className='text-sm font-medium'>
                    {uploadedFiles.length} {uploadedFiles.length === 1 ? 'קובץ נבחר' : 'קבצים נבחרו'}
                  </p>
                  {mergedFile && <p className='text-xs text-gray-500'>גודל סופי: {(mergedFile.size / 1024 / 1024).toFixed(2)} MB</p>}
                  {isMerging && <p className='text-xs text-blue-600'>מאחד קבצים...</p>}
                </div>
              )}
            </div>
            <div className='flex gap-2'>
              <Button variant='outline' size='sm' onClick={handleClick}>
                הוסף עוד
              </Button>
              <Button variant='outline' size='sm' onClick={handleRemoveAll}>
                הסר הכל
              </Button>
            </div>
          </div>

          {/* List View - Multiple files */}
          {filePreviews.length > 1 && (
            <div className='mb-4 space-y-2'>
              {filePreviews.map((item, index) => (
                <div key={index} className='flex items-center gap-3 rounded border p-2'>
                  <div className='flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded border bg-gray-50'>
                    {item.preview ? (
                      <img src={item.preview} alt={item.file.name} className='h-full w-full object-cover' />
                    ) : (
                      <span className='text-xs text-gray-400'>PDF</span>
                    )}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm font-medium'>{item.file.name}</p>
                    <p className='text-xs text-gray-500'>{(item.file.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <Button variant='ghost' size='sm' onClick={() => handleRemoveFile(index)} className='flex-shrink-0'>
                    ✕
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Large Preview - Single file (image or PDF) */}
          {filePreviews.length === 1 && (
            <div className='mb-4'>
              {filePreviews[0].preview ? (
                <div className='flex justify-center'>
                  <img src={filePreviews[0].preview} alt='File preview' className='max-h-64 w-auto rounded border' />
                </div>
              ) : (
                <div className='w-full'>
                  <div id='adobe-pdf-view-uploaded' className='h-96 w-full rounded'></div>
                </div>
              )}
            </div>
          )}

          {/* Existing File Preview - From server (when editing) */}
          {previewUrl && uploadedFiles.length === 0 && (
            <>
              {isPreviewImage && (
                <div className='flex justify-center'>
                  <img src={previewUrl} alt='File preview' className='max-h-64 w-auto rounded border' />
                </div>
              )}
              {isPreviewPdf && (
                <div className='w-full'>
                  <div id='adobe-pdf-view' className='h-96 w-full rounded'></div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
